import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { boardAPI, adminAPI } from '../api';
import { useAuthStore } from '../store';
import './MainPage.css';

// 메뉴명 기반 아이콘 매핑
const MENU_ICON_MAP: Record<string, string> = {
  '국가기사시험': '🏆',
  '전기기사': '⚡',
  '전자기사': '🔌',
  '토목기사': '🏗️',
  '건설기사': '🔨',
  '기계기사': '⚙️',
  '화학기사': '🧪',
  '정보처리기사': '💻',
  '아두이노': '🤖',
  '커뮤니티': '💬',
  '게시판': '📋',
  '공지사항': '📢',
  '자료실': '📁',
  '갤러리': '🖼️',
  '일정': '📅',
  '캘린더': '📆',
  '동영상': '🎬',
  '유튜브': '▶️',
  '링크': '🔗',
  '홈': '🏠',
  '설정': '⚙️',
};

// 시험 관련 키워드 → /exam으로 이동
const EXAM_KEYWORDS = ['기사', '기능사', '기능장', '산업기사', '시험', 'CBT', '국가기술자격'];

const getMenuIcon = (name: string, savedIcon?: string): string => {
  if (savedIcon && savedIcon !== '📌') return savedIcon;
  for (const key of Object.keys(MENU_ICON_MAP)) {
    if (name.includes(key)) return MENU_ICON_MAP[key];
  }
  return '📂';
};

const isExamMenu = (name: string): boolean =>
  EXAM_KEYWORDS.some(k => name.includes(k));

// 드롭다운 메뉴 카드 컴포넌트
const BOARD_TYPE_KEYWORDS: Record<string, string> = {
  '커뮤니티': 'community',
  '자유게시판': 'free',
  'Q&A': 'qna',
  'QNA': 'qna',
};

const getBoardType = (menuName: string): string | null => {
  for (const [keyword, boardType] of Object.entries(BOARD_TYPE_KEYWORDS)) {
    if (menuName.includes(keyword)) return boardType;
  }
  return null;
};

const MenuCard: React.FC<{ menu: any; isLoggedIn: boolean; categoryMap: Record<string, string[]> }> = ({ menu, isLoggedIn, categoryMap }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // 수동 서브메뉴
  const manualSubs = menu.sub_menus
    ? menu.sub_menus.split(',').map((s: string) => s.trim()).filter(Boolean)
    : [];

  // 게시판 타입 자동 감지 → 카테고리 DB에서 서브메뉴 자동 로드
  const boardType = getBoardType(menu.name);
  const autoSubs = boardType && categoryMap[boardType] ? categoryMap[boardType] : [];

  // 자동 카테고리 우선, 없으면 수동 서브메뉴 사용
  const subs = autoSubs.length > 0 ? autoSubs : manualSubs;

  const examMenu = isExamMenu(menu.name);

  const handleCardClick = () => {
    if (subs.length > 0) {
      setOpen(!open);
      return;
    }
    if (examMenu) {
      if (!isLoggedIn) { navigate('/login'); return; }
      navigate('/exam');
    } else if (menu.url) {
      if (menu.url === '/calendar') return;
      if (menu.url.startsWith('/')) {
        if (menu.require_login && !isLoggedIn) { navigate('/login'); return; }
        navigate(menu.url);
      } else {
        window.open(menu.url, '_blank');
      }
    } else {
      // ✅ URL/서브메뉴 없는 단독 메뉴(폴더) → 메뉴명을 카테고리로 하는 전용 게시판 자동 연결
      //    (추억앨범, 소소한 이야기 등 → 클릭 시 해당 이름 게시판 + 글쓰기 자동 제공)
      if (menu.require_login && !isLoggedIn) { navigate('/login'); return; }
      navigate(`/board?category=${encodeURIComponent(menu.name)}`);
    }
  };

  const handleSubClick = (sub: string) => {
    if (sub.startsWith('http')) {
      window.open(sub, '_blank');
      return;
    }
    if (examMenu) {
      if (!isLoggedIn) { navigate('/login'); return; }
      navigate(`/exam?subject=${encodeURIComponent(sub)}`);
      return;
    }
    // 게시판 카테고리 → /board?category=
    if (menu.require_login && !isLoggedIn) { navigate('/login'); return; }
    const base = menu.url?.startsWith('/') ? menu.url.split('?')[0] : '/board';
    navigate(`${base}?category=${encodeURIComponent(sub)}`);
  };

  return (
    <div className={`menu-card ${open ? 'menu-card-open' : ''}`}>
      <div className="menu-card-inner" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
        <div className="menu-icon">{getMenuIcon(menu.name, menu.icon)}</div>
        <div className="menu-name">{menu.name}</div>
        {subs.length > 0 && (
          <div className="menu-arrow">{open ? '▲' : '▼'}</div>
        )}
      </div>

      {open && subs.length > 0 && (
        <div className="menu-dropdown">
          {subs.map((sub: string, i: number) => (
            <button key={i} className="menu-dropdown-item" onClick={() => handleSubClick(sub)}>
              <span className="menu-dropdown-dot" />
              {sub}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MainPage: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuthStore();
  const [notices, setNotices] = useState<any[]>([]);
  const [menus, setMenus] = useState<any[]>([]);
  const [categoryMap, setCategoryMap] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [selectedNotice, setSelectedNotice] = useState<any | null>(null);

  // ✅ 상단 배너 이미지
  const [heroImages, setHeroImages] = useState<string[]>([]);
  const [heroInterval, setHeroInterval] = useState(5);
  const [heroIdx, setHeroIdx] = useState(0);
  // ✅ 오른쪽 배너 이미지 (별도)
  const [heroImages2, setHeroImages2] = useState<string[]>([]);

  // 슬라이더 자동 로테이션
  useEffect(() => {
    if (heroImages.length < 2) return;
    const timer = setInterval(() => {
      setHeroIdx(i => (i + 1) % heroImages.length);
    }, heroInterval * 1000);
    return () => clearInterval(timer);
  }, [heroImages, heroInterval]);

  useEffect(() => {
    const load = async () => {
      try {
        const [noticeRes, menuRes, catRes, settingsRes] = await Promise.all([
          boardAPI.notices(),
          adminAPI.menus(),
          boardAPI.categories(),
          adminAPI.settings(),
        ]);
        setNotices(noticeRes.data.slice(0, 5));
        setMenus(menuRes.data);

        // ✅ 이미지 URL 파싱 헬퍼
        const parseImgs = (raw: string) => raw
          ? raw.split('\n').map((s: string) => {
              const trimmed = s.trim();
              if (!trimmed) return null;
              if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
              return trimmed.startsWith('/') ? trimmed : `/images/image_slider/${trimmed}`;
            }).filter(Boolean) as string[]
          : [];

        // 상단 배너 이미지
        setHeroImages(parseImgs(settingsRes.data.hero_images || ''));
        setHeroInterval(settingsRes.data.hero_interval || 5);
        // 오른쪽 배너 이미지 (별도)
        setHeroImages2(parseImgs(settingsRes.data.hero_images2 || ''));

        const map: Record<string, string[]> = {};
        catRes.data.forEach((c: any) => {
          if (!map[c.board_type]) map[c.board_type] = [];
          map[c.board_type].push(c.name);
        });
        setCategoryMap(map);
      } catch (e) {}
      setLoading(false);
    };
    load();
  }, []);

  return (
    <Layout>
      {/* ✅ 상단 배너 섹션 - main-page 컨테이너 밖 → 전체 너비 */}
      <section className="banner-section">
          {heroImages.length > 0 ? (
            <div className="banner-grid" style={{
              display: 'grid',
              gridTemplateColumns: heroImages.length === 1 ? '1fr' : heroImages.length === 2 ? '1fr 1fr' : '1fr 1fr 1fr',
              gap: 0,
              width: '100%',
            }}>
              {heroImages.map((src, i) => (
                <div key={i} className="banner-item" style={{ position: 'relative', overflow: 'hidden' }}>
                  <img src={src} alt={`배너 ${i+1}`}
                    style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }}
                    onError={e => (e.currentTarget.style.display = 'none')} />
                </div>
              ))}
            </div>
          ) : (
            /* ✅ 이미지 없을 때 전기 회로도 SVG 폴백 */
            <div style={{ width: '100%', background: '#f8fafc' }}>
              <svg viewBox="0 0 1200 260" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: 260, display: 'block' }}>
                <rect width="1200" height="260" fill="#f8fafc"/>
                <text x="600" y="36" textAnchor="middle" fontSize="20" fontWeight="700" fill="#2563eb">⚡ 기본 전기 회로</text>
                <line x1="250" y1="90" x2="950" y2="90" stroke="#334155" strokeWidth="3"/>
                <line x1="250" y1="210" x2="950" y2="210" stroke="#334155" strokeWidth="3"/>
                <line x1="250" y1="90" x2="250" y2="210" stroke="#334155" strokeWidth="3"/>
                <line x1="950" y1="90" x2="950" y2="210" stroke="#334155" strokeWidth="3"/>
                <line x1="232" y1="132" x2="268" y2="132" stroke="#334155" strokeWidth="5"/>
                <line x1="238" y1="148" x2="262" y2="148" stroke="#334155" strokeWidth="2.5"/>
                <line x1="232" y1="162" x2="268" y2="162" stroke="#334155" strokeWidth="5"/>
                <text x="250" y="194" textAnchor="middle" fontSize="14" fill="#64748b">E (전원)</text>
                <rect x="420" y="74" width="100" height="32" rx="5" fill="white" stroke="#f39c12" strokeWidth="2.5"/>
                <text x="470" y="95" textAnchor="middle" fontSize="15" fontWeight="700" fill="#f39c12">R₁ 100Ω</text>
                <rect x="680" y="74" width="100" height="32" rx="5" fill="white" stroke="#f39c12" strokeWidth="2.5"/>
                <text x="730" y="95" textAnchor="middle" fontSize="15" fontWeight="700" fill="#f39c12">R₂ 200Ω</text>
                <circle cx="950" cy="150" r="26" fill="white" stroke="#2ecc71" strokeWidth="2.5"/>
                <line x1="938" y1="138" x2="962" y2="162" stroke="#2ecc71" strokeWidth="2.5"/>
                <line x1="962" y1="138" x2="938" y2="162" stroke="#2ecc71" strokeWidth="2.5"/>
                <text x="950" y="192" textAnchor="middle" fontSize="13" fill="#64748b">전구 L</text>
                <circle cx="560" cy="210" r="6" fill="white" stroke="#334155" strokeWidth="2.5"/>
                <circle cx="620" cy="210" r="6" fill="white" stroke="#334155" strokeWidth="2.5"/>
                <line x1="566" y1="208" x2="614" y2="196" stroke="#334155" strokeWidth="2.5"/>
                <text x="590" y="234" textAnchor="middle" fontSize="14" fill="#64748b">스위치 S</text>
                <text x="600" y="252" textAnchor="middle" fontSize="17" fontWeight="700" fill="#7c3aed">V = IR (옴의 법칙)</text>
              </svg>
            </div>
          )}
      </section>

      <div className="main-page">
        <section className="hero">
          <div className="hero-content">
            <div className="hero-badge">⚡ 전기기사 CBT 학습 플랫폼</div>
            <h1 className="hero-title">
              합격을 향한<br />
              <span className="hero-highlight">스마트 학습</span>
            </h1>
            <p className="hero-desc">
              필기·실기 문제풀이, 모의고사, 오답노트까지<br />
              체계적인 학습 시스템으로 합격을 준비하세요.
            </p>
            <div className="hero-actions" style={{ justifyContent: 'flex-start' }}>
              <button className="btn btn-lg"
                style={{ background: '#f39c12', color: 'white', border: 'none' }}
                onClick={() => navigate('/trial?type=필기')}>
                🎯 필기 체험
              </button>
              <button className="btn btn-lg"
                style={{ background: '#2ecc71', color: 'white', border: 'none' }}
                onClick={() => navigate('/trial?type=실기')}>
                🔧 실기 체험
              </button>
            </div>
          </div>

          {/* ✅ hero 오른쪽 배너 - heroImages2 독립 사용 (1~3개 자동 분할) */}
          <div className="hero-visual">
            {heroImages2.length > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: heroImages2.length === 1 ? '1fr' : heroImages2.length === 2 ? '1fr 1fr' : '1fr 1fr 1fr',
                gap: 6,
                width: '100%',
                height: '100%',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-lg)',
              }}>
                {heroImages2.map((src, i) => (
                  <img key={i} src={src} alt={`배너 ${i+1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 260 }}
                    onError={e => (e.currentTarget.style.display = 'none')} />
                ))}
              </div>
            ) : (
              <div className="hero-image-area">
                <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg">
                  <rect width="400" height="300" fill="#f8fafc" rx="12"/>
                  <text x="200" y="28" textAnchor="middle" fontSize="13" fontWeight="700" fill="#2563eb">⚡ 기본 전기 회로</text>
                  <line x1="60" y1="80" x2="340" y2="80" stroke="#334155" strokeWidth="2.5"/>
                  <line x1="60" y1="220" x2="340" y2="220" stroke="#334155" strokeWidth="2.5"/>
                  <line x1="60" y1="80" x2="60" y2="220" stroke="#334155" strokeWidth="2.5"/>
                  <line x1="340" y1="80" x2="340" y2="220" stroke="#334155" strokeWidth="2.5"/>
                  <line x1="45" y1="138" x2="75" y2="138" stroke="#334155" strokeWidth="4"/>
                  <line x1="50" y1="152" x2="70" y2="152" stroke="#334155" strokeWidth="2"/>
                  <line x1="45" y1="162" x2="75" y2="162" stroke="#334155" strokeWidth="4"/>
                  <text x="60" y="195" textAnchor="middle" fontSize="11" fill="#64748b">E (전원)</text>
                  <rect x="110" y="68" width="60" height="24" rx="4" fill="white" stroke="#f39c12" strokeWidth="2"/>
                  <text x="140" y="84" textAnchor="middle" fontSize="11" fontWeight="700" fill="#f39c12">R₁ 100Ω</text>
                  <rect x="230" y="68" width="60" height="24" rx="4" fill="white" stroke="#f39c12" strokeWidth="2"/>
                  <text x="260" y="84" textAnchor="middle" fontSize="11" fontWeight="700" fill="#f39c12">R₂ 200Ω</text>
                  <circle cx="340" cy="150" r="18" fill="white" stroke="#2ecc71" strokeWidth="2"/>
                  <line x1="332" y1="142" x2="348" y2="158" stroke="#2ecc71" strokeWidth="2"/>
                  <line x1="348" y1="142" x2="332" y2="158" stroke="#2ecc71" strokeWidth="2"/>
                  <text x="340" y="182" textAnchor="middle" fontSize="10" fill="#64748b">전구 L</text>
                  <circle cx="170" cy="220" r="4" fill="white" stroke="#334155" strokeWidth="2"/>
                  <circle cx="210" cy="220" r="4" fill="white" stroke="#334155" strokeWidth="2"/>
                  <line x1="174" y1="218" x2="206" y2="210" stroke="#334155" strokeWidth="2"/>
                  <text x="190" y="240" textAnchor="middle" fontSize="11" fill="#64748b">스위치 S</text>
                  <text x="200" y="270" textAnchor="middle" fontSize="12" fontWeight="700" fill="#7c3aed">V = IR (옴의 법칙)</text>
                </svg>
              </div>
            )}
          </div>
        </section>

        {menus.length > 0 && (
          <section className="menu-section">
            <div className="section-title">메뉴</div>
            <div className="menu-grid">
              {menus.map((menu: any) => (
                <MenuCard key={menu.id} menu={menu} isLoggedIn={isLoggedIn} categoryMap={categoryMap} />
              ))}
            </div>
          </section>
        )}

        <section className="bottom-section" style={{ display: 'block' }}>
          <div className="card">
            <div className="card-header">📢 공지사항</div>
            <div className="card-body" style={{ padding: 0 }}>
              {loading ? (
                <div className="loading-spinner"><div className="spinner" /></div>
              ) : notices.length === 0 ? (
                <div className="empty-state">
                  <div className="icon">📭</div>
                  <p>등록된 공지사항이 없습니다.</p>
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr><th>번호</th><th>제목</th><th>작성일</th><th>조회</th></tr>
                  </thead>
                  <tbody>
                    {notices.map((notice: any) => (
                      <tr key={notice.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedNotice(notice)}>
                        <td style={{ width: 60, textAlign: 'center' }}>
                          {notice.is_important
                            ? <span className="badge badge-danger">중요</span>
                            : notice.id}
                        </td>
                        <td>{notice.title}</td>
                        <td style={{ width: 100, color: 'var(--text-muted)', fontSize: 12 }}>
                          {notice.created_at?.slice(0, 10)}
                        </td>
                        <td style={{ width: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
                          {notice.views}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>


        </section>
      </div>{/* end main-page */}

      {/* ✅ 공지사항 상세 모달 */}
      {selectedNotice && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setSelectedNotice(null)}>
          <div style={{
            background: 'white', borderRadius: 16, width: 560,
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>
            {/* 모달 헤더 */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {selectedNotice.is_important && <span className="badge badge-danger">중요</span>}
                <span style={{ fontWeight: 800, fontSize: 15 }}>{selectedNotice.title}</span>
              </div>
              <button onClick={() => setSelectedNotice(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>✕</button>
            </div>
            {/* 메타 정보 */}
            <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-muted)' }}>
              <span>👤 {selectedNotice.author || '관리자'}</span>
              <span>📅 {selectedNotice.created_at?.slice(0, 10)}</span>
              <span>👁️ {selectedNotice.views}</span>
            </div>
            {/* 내용 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px', fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>
              {selectedNotice.content}
            </div>
            {/* 닫기 버튼 */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary btn-sm" onClick={() => setSelectedNotice(null)}>확인</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default MainPage;
